import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Lock, KeyRound, Database, FileX2, ShieldCheck, UserX, Globe2, FileSignature } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { LineMask, FadeIn, SectionTag } from "../components/marketing/Reveal";

const PRACTICES = [
  { icon: Lock, t: "Encrypted in transit", d: "All portal traffic — case data, scans, photos and X-rays — travels over HTTPS/TLS encryption between your browser and our systems." },
  { icon: KeyRound, t: "Access controls", d: "Case files are visible only to the submitting account and the assigned OrthoPlaniX planning team. Role-based access separates doctors, planners and administrators." },
  { icon: Database, t: "Secure cloud storage", d: "Uploaded files are stored in access-controlled cloud object storage, retrieved through authenticated, expiring links — never public URLs." },
  { icon: UserX, t: "Data minimization", d: "We ask you to submit de-identified patient references (initials + chart number) rather than full patient names. We plan teeth, not identities." },
  { icon: FileX2, t: "Retention & deletion", d: "Case archives remain available to your account for ongoing treatment reference. On written request, we delete case data associated with your account." },
  { icon: FileSignature, t: "Confidentiality & NDA", d: "Our team operates under confidentiality obligations. Lab and enterprise partners can put mutual NDAs and data-processing agreements in place before the first case." },
  { icon: ShieldCheck, t: "Doctor-controlled workflow", d: "No plan is released to production without the treating doctor's digital approval. The clinical record of who approved what, and when, is kept per case." },
  { icon: Globe2, t: "International awareness", d: "We serve dental professionals across many jurisdictions and structure our handling of patient data around data-minimization and confidentiality principles common to international privacy frameworks." },
];

export default function Security() {
  return (
    <MarketingLayout>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <section className="pt-40 pb-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <SectionTag>Security &amp; Privacy</SectionTag>
            <h1 className="font-display font-black tracking-tight leading-[0.98] text-[clamp(2.6rem,6.5vw,5.5rem)] max-w-4xl">
              <LineMask delay={0.1}>Patient data,</LineMask>
              <LineMask delay={0.25} className="text-neon">treated clinically.</LineMask>
            </h1>
            <FadeIn delay={0.5}>
              <p className="mt-8 max-w-2xl text-base md:text-lg text-slate-400 leading-relaxed">
                You are trusting us with scans, X-rays and clinical photographs. Here is exactly how that data is handled — from upload to deletion.
              </p>
            </FadeIn>
          </div>
        </section>

        <section className="pb-24 lg:pb-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {PRACTICES.map((p, i) => (
              <FadeIn key={p.t} delay={i * 0.05} className="h-full">
                <div className="h-full bg-white/5 border border-white/10 rounded-2xl p-6 lg:p-7 transition-colors duration-500 hover:border-neon/40" data-testid={`security-card-${i}`}>
                  <p.icon className="h-6 w-6 text-neon mb-4" />
                  <p className="font-display font-bold text-base lg:text-lg">{p.t}</p>
                  <p className="text-xs text-slate-500 mt-2 leading-relaxed">{p.d}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </section>

        <section className="pb-24 lg:pb-32">
          <div className="max-w-5xl mx-auto px-6 lg:px-10">
            <FadeIn>
              <div className="rounded-2xl border border-white/10 bg-ink-2/60 p-8 lg:p-10">
                <p className="text-[10px] uppercase tracking-[0.3em] text-neon mb-4">A note on compliance</p>
                <p className="text-sm text-slate-400 leading-relaxed">
                  Regulatory frameworks such as HIPAA and GDPR place obligations on the treating practice as the data controller. OrthoPlaniX supports your compliance posture through data minimization, access controls, encryption in transit and contractual confidentiality — and we are happy to complete data-processing agreements with lab and enterprise partners. Specific questions about your jurisdiction? <Link to="/contact" className="text-neon hover:underline">Talk to us</Link> before submitting your first case.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.15}>
              <div className="mt-14 text-center">
                <Link to="/register" data-testid="security-cta"
                  className="group inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_50px_rgba(0,229,255,0.5)]">
                  Submit a case securely <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </FadeIn>
          </div>
        </section>
      </motion.div>
    </MarketingLayout>
  );
}
